import { useCallback, useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { audioParts } from "@/lib/audio";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const VoiceInput = ({ context, testId, onUse, disabled = false, onBusyChange }) => {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState("idle");
  const [seconds, setSeconds] = useState(0);
  const [progress, setProgress] = useState("");
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const control = useRef({ generation: 0 });
  const busyCallback = useRef(onBusyChange); busyCallback.current = onBusyChange;
  const busy = ["requesting", "recording", "transcribing"].includes(phase);
  useEffect(() => { busyCallback.current?.(busy); }, [busy]);

  const release = useCallback(() => {
    const c = control.current;
    clearTimeout(c.timeout); clearInterval(c.timer);
    c.stream?.getTracks().forEach(t => t.stop()); c.stream = null;
  }, []);
  const cancel = useCallback((update = true) => {
    const c = control.current; c.generation++; c.abort?.abort();
    if (c.recorder?.state === "recording") c.recorder.stop();
    c.recorder = null; release();
    if (update) { setOpen(false); setPhase("idle"); setText(""); setError(""); }
    busyCallback.current?.(false);
  }, [release]);
  useEffect(() => () => cancel(false), [cancel]); // stop microphone/network when leaving the page

  const processRecording = async (blob, generation) => {
    const c = control.current;
    if (generation !== c.generation) return;
    setPhase("transcribing"); setProgress("Preparing temporary audio…");
    const abort = new AbortController(); c.abort = abort;
    let parts = [];
    let result = "";
    try {
      parts = await audioParts(blob);
      const recordingId = crypto.randomUUID();
      for (let i = 0; i < parts.length; i++) {
        if (abort.signal.aborted || generation !== c.generation) return;
        setProgress(`Transcribing part ${i + 1} of ${parts.length}…`);
        const { data } = await api.post("/speech/transcribe", parts[i], { signal: abort.signal,
          params: { context, recording_id: recordingId, part: i }, headers: { "Content-Type": "audio/wav" } });
        result = [result, data.text].filter(Boolean).join(" ");
        parts[i] = null;
      }
      if (generation !== c.generation) return;
      setText(result); setPhase("review");
      if (!result.trim()) setError("No speech was detected. Record again or type your text.");
    } catch (e) {
      if (generation !== c.generation || abort.signal.aborted) return;
      setText(result); setError(`${e?.response ? apiError(e) : e.message || "Transcription failed."}${result ? " This is only a partial transcript; review it before using it." : ""}`); setPhase("review");
    } finally { parts.length = 0; blob = null; }
  };

  const stop = () => { const c = control.current; clearTimeout(c.timeout); clearInterval(c.timer); if (c.recorder?.state === "recording") c.recorder.stop(); };
  const start = async () => {
    cancel(false);
    const c = control.current; const generation = c.generation;
    setOpen(true); setPhase("requesting"); setText(""); setError(""); setSeconds(0); setProgress("");
    let chunks = [];
    try {
      if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder || !(window.AudioContext || window.webkitAudioContext)) throw new Error("Voice recording isn't supported here. Use a current browser on HTTPS, or type your text.");
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (generation !== c.generation) { stream.getTracks().forEach(t => t.stop()); return; }
      c.stream = stream;
      const mimeType = ["audio/webm;codecs=opus", "audio/mp4", "audio/webm"].find(m => MediaRecorder.isTypeSupported(m));
      const recorder = new MediaRecorder(stream, { audioBitsPerSecond: 64000, ...(mimeType ? { mimeType } : {}) });
      c.recorder = recorder;
      recorder.ondataavailable = e => { if (e.data.size && generation === c.generation) chunks.push(e.data); };
      recorder.onerror = () => { if (generation === c.generation) { cancel(false); setPhase("review"); setError("Recording stopped unexpectedly. Your typed text is unchanged; record again."); } };
      recorder.onstop = () => {
        if (generation !== c.generation) { chunks = []; return; }
        const blob = new Blob(chunks, { type: recorder.mimeType }); chunks = []; release();
        processRecording(blob, generation);
      };
      recorder.start(500); setPhase("recording");
      c.timer = setInterval(() => setSeconds(s => s + 1), 1000);
      c.timeout = setTimeout(stop, 120000);
    } catch (e) {
      if (generation !== c.generation) return;
      release(); setPhase("review");
      setError(e.name === "NotAllowedError" ? "Microphone permission was denied. You can allow it in browser settings, record again, or keep typing." : e.message || "Couldn't start the microphone.");
    }
  };
  return <>
    <Button type="button" variant="outline" size="sm" data-testid={`${testId}-start`} disabled={disabled || busy} onClick={start} className="rounded-full"><Mic className="mr-1.5 h-3.5 w-3.5" />Dictate</Button>
    <Dialog open={open} onOpenChange={next => { if (!next) cancel(); }}><DialogContent data-testid={`${testId}-dialog`} className="max-h-[90dvh] overflow-y-auto rounded-2xl bg-white">
      <DialogHeader><DialogTitle>Say it, then make it yours</DialogTitle><DialogDescription>Up to two minutes. After stopping, audio goes to managed Whisper (whisper-1). NxT doesn't save the recording; the provider's processing policy still applies. Nothing is sent or saved to your career tools until you choose.</DialogDescription></DialogHeader>
      <p data-testid={`${testId}-status`} aria-live="polite" className="text-sm">{phase === "recording" ? `Recording · ${seconds}s / 120s` : phase === "requesting" ? "Waiting for microphone permission…" : phase === "transcribing" ? progress : "Review and edit your transcript"}</p>
      {phase === "recording" && <Button type="button" data-testid={`${testId}-stop`} onClick={stop} className="rounded-full bg-rose-700"><Square className="mr-2 h-4 w-4" />Stop & transcribe</Button>}
      {phase === "transcribing" && <Loader2 aria-label="Transcribing" className="h-5 w-5 animate-spin" />}
      {error && <p role="alert" data-testid={`${testId}-error`} className="rounded-xl bg-amber-50 p-3 text-sm text-amber-900">{error}</p>}
      {phase === "review" && <><label htmlFor={`${testId}-text`} className="text-sm font-bold">Your words</label><textarea id={`${testId}-text`} data-testid={`${testId}-transcript`} rows={7} maxLength={20000} value={text} onChange={e => setText(e.target.value)} className="w-full rounded-xl border border-input p-3 text-sm" /><p className="text-xs text-muted-foreground" data-testid={`${testId}-review-note`}>Check names and words. Using this text adds it to your draft; it does not submit it.</p></>}
      <div className="flex flex-wrap justify-end gap-2"><Button type="button" variant="outline" data-testid={`${testId}-cancel`} onClick={() => cancel()}>Discard</Button>{!busy && <Button type="button" variant="outline" data-testid={`${testId}-retry`} onClick={start}>Record again</Button>}{phase === "review" && <Button type="button" data-testid={`${testId}-use`} disabled={!text.trim()} onClick={() => { if (onUse(text.trim()) !== false) cancel(); }}>Use this text</Button>}</div>
    </DialogContent></Dialog>
  </>;
};
